/**
 * Phasing risks — PP pack PP9.
 *
 * Auto-derive risks from the phased ProjectIntent + expanded intent
 * fields. Pure function — returns a list of structured risks the report
 * renderer can merge into its existing risk surface.
 *
 * Rules implemented:
 *
 *   1. Sequencing risk: when Phase N depends on Phase N-1 commissioning
 *      with < 12 month buffer → "Tight phase-to-phase handoff" amber.
 *
 *   2. Technology-readiness risk: when a phase uses SMR with COD before
 *      2028 → "Technology-readiness risk for Phase N" high.
 *
 *   3. Carbon-commitment misalignment: when carbonCommitment = 're100'
 *      AND any phase uses gas-cc / gas-sc → "Phase N gas turbine
 *      conflicts with RE100" amber.
 *
 *   4. Tier-redundancy mismatch: tier IV + N / N+1 triggers a
 *      fault-tolerant topology review, not an assumed certification rule.
 *
 *   5. PUE infeasibility hook: declared PUE (pueValue, legacy pueTarget) < 1.2 surfaces an advisory
 *      that climate wet-bulb should be checked (we don't have climate
 *      data in this pure function — flag for the report renderer).
 *
 * Categories:
 *   - 'sequencing' | 'technology' | 'carbon' | 'tier' | 'pue'
 */
import type { ProjectIntent, DetailedRisk, SiteRagReport } from '../types/siteRag';
import {
  assessPhaseContingency,
  blockCountsFromScenarioUnits,
  resolvePromotedScenarioPhaseContingency,
  type PhaseBlockCounts,
  type SolvedScenarioPhaseContingency,
} from './phaseContingency';
import { assessPhaseLeadTime } from './phaseLeadTime';
import { effectivePhases } from './projectIntent';
import { assessPhaseFeasibility, firmCapacitySiteContextFromIntent } from './phaseFeasibility';
import { isValidPue } from './powerStack';

export type PhasingRiskCategory =
  | 'sequencing'
  | 'technology'
  | 'carbon'
  | 'tier'
  | 'pue'
  | 'feasibility'
  | 'contingency'
  | 'lead-time';

export type PhasingRiskSeverity = 'low' | 'medium' | 'high';

export interface PhasingRisk {
  id: string;
  category: PhasingRiskCategory;
  severity: PhasingRiskSeverity;
  label: string;
  description: string;
  /** When the risk pertains to a specific phase, its id. */
  phaseId?: string;
}

const MS_PER_MONTH = (365.25 / 12) * 24 * 60 * 60 * 1000;
const SMR_COMMERCIAL_YEAR = 2028;
const TIGHT_HANDOFF_MONTHS = 12;

export interface DerivePhasingRisksOptions {
  blockCounts?: PhaseBlockCounts;
  solvedPhaseContingency?: SolvedScenarioPhaseContingency | null;
}

/**
 * Derive phasing + expanded-intent risks from an intent. Returns an
 * empty array when no risks apply.
 */
export function derivePhasingRisks(
  intent: ProjectIntent | undefined | null,
  nowOrOptions: Date | DerivePhasingRisksOptions = new Date(),
  maybeOptions: DerivePhasingRisksOptions = {},
): PhasingRisk[] {
  if (!intent) return [];
  const now = nowOrOptions instanceof Date ? nowOrOptions : new Date();
  const options = nowOrOptions instanceof Date ? maybeOptions : nowOrOptions;
  const risks: PhasingRisk[] = [];
  const view = effectivePhases(intent);

  // Rule 1: Sequencing — phase-to-phase handoff < 12 months
  if (view.isPhased) {
    for (let i = 1; i < view.phases.length; i += 1) {
      const prev = view.phases[i - 1];
      const cur = view.phases[i];
      if (!prev.targetInServiceDate || !cur.targetInServiceDate) continue;
      const prevMs = Date.parse(prev.targetInServiceDate);
      const curMs = Date.parse(cur.targetInServiceDate);
      if (!Number.isFinite(prevMs) || !Number.isFinite(curMs)) continue;
      const gapMonths = (curMs - prevMs) / MS_PER_MONTH;
      if (gapMonths < 0) {
        risks.push({
          id: `phasing-seq-${cur.id}`,
          category: 'sequencing',
          severity: 'high',
          label: `Phase ${cur.sequence} COD precedes Phase ${prev.sequence}`,
          description: `The cumulative build sequence runs backwards in time. Confirm phase ordering and commissioning dates before using this schedule.`,
          phaseId: cur.id,
        });
      }
      if (gapMonths >= 0 && gapMonths < TIGHT_HANDOFF_MONTHS) {
        risks.push({
          id: `phasing-seq-${cur.id}`,
          category: 'sequencing',
          severity: 'medium',
          label: `Tight handoff: Phase ${cur.sequence} starts ${Math.round(gapMonths)}mo after Phase ${prev.sequence}`,
          description: `Phase ${cur.sequence} (${cur.label}) COD is ${Math.round(gapMonths)} months after Phase ${prev.sequence}. Common practice is ≥12 months between phase commissionings to keep workforce, supply chain, and grid-interconnection load manageable. Tight handoff increases sequencing risk if Phase ${prev.sequence} slips.`,
          phaseId: cur.id,
        });
      }
    }
  }

  // Rule 2: Technology-readiness — SMR before 2028
  if (view.isPhased) {
    for (const phase of view.phases) {
      if (phase.powerSource !== 'smr' && !phase.powerSourceConfig?.components.includes('smr')) continue;
      if (!phase.targetInServiceDate) continue;
      const year = parseInt(phase.targetInServiceDate.slice(0, 4), 10);
      if (Number.isFinite(year) && year < SMR_COMMERCIAL_YEAR) {
        risks.push({
          id: `phasing-tech-readiness-${phase.id}`,
          category: 'technology',
          severity: 'high',
          label: `SMR readiness risk: Phase ${phase.sequence} has an early COD (${year})`,
          description: `Phase ${phase.sequence} (${phase.label}) targets SMR commercial operation before the internal ${SMR_COMMERCIAL_YEAR} screening benchmark. Verify the selected design, jurisdiction-specific licensing path, vendor delivery evidence and fallback supply. This benchmark is a planning flag; a later date does not establish commercial readiness.`,
          phaseId: phase.id,
        });
      }
    }
  }

  // Rule 3: Carbon commitment misalignment
  if (intent.carbonCommitment === 're100' || intent.carbonCommitment === '24-7-cfe') {
    const gasPhases = view.phases.filter(p => p.powerSource === 'gas-cc' || p.powerSource === 'gas-sc'
      || p.powerSourceConfig?.components.some(c => c === 'recip' || c === 'gas-turbine'));
    for (const phase of gasPhases) {
      risks.push({
        id: `phasing-carbon-${phase.id}`,
        category: 'carbon',
        severity: 'medium',
        label: `Phase ${phase.sequence} gas generation conflicts with ${intent.carbonCommitment} commitment`,
        description: `Phase ${phase.sequence} (${phase.label}) includes gas generation, while the declared commitment is ${intent.carbonCommitment}. Document how the proposed supply meets that commitment's accounting and timing requirements; do not assume offsets or carbon capture qualify as renewable electricity or hourly carbon-free supply.`,
        phaseId: phase.id,
      });
    }
  }

  // Rule 4: Tier vs redundancy mismatch
  if (intent.datacenterTierTarget === 'IV' && (intent.redundancyClass === 'N' || intent.redundancyClass === 'N+1')) {
    risks.push({
      id: 'phasing-tier-redundancy',
      category: 'tier',
      severity: 'high',
      label: 'Tier IV target needs fault-tolerant topology review',
      description: `The declared Tier IV target and ${intent.redundancyClass} capacity label do not demonstrate fault tolerance. Verify independent distribution paths, compartmentalization and continuous cooling. Uptime Institute evaluates the complete topology; neither 2N nor 2N+1 alone establishes certification.`,
    });
  }
  if (intent.datacenterTierTarget === 'III' && intent.redundancyClass === 'N') {
    risks.push({
      id: 'phasing-tier-redundancy-III',
      category: 'tier',
      severity: 'medium',
      label: 'Tier III target without N+1 redundancy',
      description: `The declared Tier III target needs concurrent maintainability. An N capacity label does not demonstrate redundant components and maintainable distribution paths. Verify the complete topology against the target.`,
    });
  }

  // Rule 6: Power-mix feasibility — an AND-combined phase whose firm capacity
  // can't cover its load + reserve margin (capacity-credit based; see
  // phaseFeasibility.ts). Advisory only — never scores the composite.
  // Same firm-capacity context every report surface grades with, so a
  // no-capacity / range-mode phase grades a real basis, not 0 MW.
  const feasibilityContext = firmCapacitySiteContextFromIntent(intent);
  for (const phase of view.phases) {
    const f = assessPhaseFeasibility(phase, feasibilityContext);
    if (!f || f.verdict === 'feasible') continue;
    risks.push({
      id: `phasing-feasibility-${phase.id}`,
      category: 'feasibility',
      severity: f.verdict === 'infeasible' ? 'high' : 'medium',
      label:
        f.verdict === 'infeasible'
          ? `Phase ${phase.sequence} power mix is infeasible for its load`
          : `Phase ${phase.sequence} power mix is short of the reserve margin`,
      description: `Phase ${phase.sequence} (${phase.label}): ${f.message}. Firm capacity uses each source's capacity credit (ELCC), not its capacity factor, plus a ${f.reserveMarginPct}% planning reserve margin. Adjust the mix sizing or add firm/storage capacity.`,
      phaseId: phase.id,
    });
  }

  // Rule 7: N-1 contingency exposure — a phase whose firm mix does not
  // survive the loss of its single largest block. This is the case the
  // capacity-credit (N-0) grade in rule 6 structurally cannot see: a phase
  // can sum to plenty of firm capacity and still hold most of it in ONE
  // machine. Advisory, like rule 6, and it reports the worst case when the
  // phase plan does not declare block counts (see phaseContingency.ts).
  for (const phase of view.phases) {
    const f = assessPhaseFeasibility(phase, feasibilityContext);
    if (!f) continue;
    const solved =
      options.solvedPhaseContingency?.phaseId === phase.id
        ? options.solvedPhaseContingency
        : null;
    const solvedPassConsistent =
      solved != null
      && solved.screenVerdict === 'pass'
      && Number.isFinite(solved.normalNetMW)
      && Number.isFinite(solved.contingencyNetMW)
      && Number.isFinite(solved.reserveRequiredMW)
      && Number.isFinite(solved.reserveMarginMW)
      && solved.normalNetMW >= 0
      && solved.contingencyNetMW >= 0
      && solved.reserveRequiredMW > 0
      && Math.abs(solved.reserveRequiredMW - f.requiredFirmMW) <= 0.5
      && solved.contingencyNetMW + 1e-6 >= solved.reserveRequiredMW
      && solved.contingencyNetMW + 1e-6 >= f.requiredFirmMW
      && solved.reserveMarginMW >= -1e-6;
    if (solvedPassConsistent) {
      continue;
    }
    const phaseBlockCounts =
      options.solvedPhaseContingency != null
      && options.solvedPhaseContingency.phaseId !== phase.id
        ? {}
        : (options.blockCounts ?? {});
    const n1 = assessPhaseContingency(f, phaseBlockCounts);
    // Only surface the exposure the N-0 grade HIDES; a phase already flagged
    // by rule 6 does not need a second, near-duplicate risk row.
    if (!n1.hiddenExposure) continue;
    risks.push({
      id: `phasing-contingency-${phase.id}`,
      category: 'contingency',
      severity: n1.n1CoversLoad ? 'medium' : 'high',
      label: `Phase ${phase.sequence} does not survive loss of its largest block`,
      description: `Phase ${phase.sequence} (${phase.label}): ${n1.statement}`,
      phaseId: phase.id,
    });
  }

  // Rule 8: Long-lead equipment vs the phase COD. A phase date can be
  // arithmetically impossible on the day it is typed, because generation
  // equipment has to be ordered years ahead. Equipment-delivery floor only
  // (see phaseLeadTime.ts); `now` is injected so the rule is deterministic.
  for (const phase of view.phases) {
    const lead = assessPhaseLeadTime(phase, now);
    if (!lead || lead.verdict === 'ok' || lead.verdict === 'indeterminate') continue;
    risks.push({
      id: `phasing-lead-time-${phase.id}`,
      category: 'lead-time',
      severity: lead.verdict === 'infeasible' ? 'high' : 'medium',
      label:
        lead.verdict === 'infeasible'
          ? `Phase ${phase.sequence} equipment cannot be delivered for its COD`
          : `Phase ${phase.sequence} equipment order window is closing`,
      description: `Phase ${phase.sequence} (${phase.label}): ${lead.statement}`,
      phaseId: phase.id,
    });
  }

  // Rule 5: PUE — flag an aggressive USER-DECLARED PUE that depends on climate.
  // The separate "Target PUE" input was removed (intent-input audit — it
  // duplicated `pueValue` one step lower in `resolvePueWithSource`), so the
  // rule reads the single declared PUE: `pueValue`, falling back to a legacy
  // persisted `pueTarget`. Same precedence as the resolver. It deliberately
  // does NOT read the config estimate / simulation PUE — those are not a
  // user-declared ambition.
  const declaredPue = isValidPue(intent.pueValue)
    ? intent.pueValue
    : intent.pueTarget;
  if (isValidPue(declaredPue) && declaredPue < 1.2) {
    risks.push({
      id: 'phasing-pue-aggressive',
      category: 'pue',
      severity: 'medium',
      label: `Aggressive PUE target ${declaredPue} — climate-dependent`,
      description: `A PUE below 1.2 needs site-specific support from the cooling design, local dry- and wet-bulb conditions, operating load and measurement boundary. Treat this as an ambition until a comparable calculation or measured result supports it; climate or a cooling label alone does not establish feasibility.`,
    });
  }

  return risks;
}

/**
 * Standing mitigation copy per phasing-risk category — paired with
 * {@link phasingRiskToDetailedRisk}. (Moved here from SiteRagReportView so the
 * lifted feasibility Risk Assessment registry section can re-derive the merged
 * risk set WITHOUT importing the view — avoiding a render-tree import cycle.)
 */
const PHASING_RISK_MITIGATION: Record<PhasingRisk['category'], string> = {
  sequencing: 'Build ≥12-month buffer between phase commissionings; track the critical path and pre-stage long-lead procurement.',
  technology: 'Carry a fallback generation technology and model the COL / FOAK schedule risk explicitly before committing the phase.',
  carbon: 'Document the bridging strategy (offsets, CCS, or replacement before the commitment deadline) to keep the carbon commitment credible.',
  tier: 'Align the redundancy class to the declared Uptime Institute tier target (or revise the tier) before final design.',
  pue: 'Validate the PUE target against the site wet-bulb / climate layer; in hot-humid regions plan for liquid cooling or a less aggressive target.',
  feasibility: 'Resize the phase power mix or add firm/storage capacity so firm-deliverable (ELCC) capacity covers load + reserve margin.',
  contingency: 'Split the concentrated capacity across more blocks (or add a redundant block) so the phase survives losing its largest single machine; declare the block count so the check runs on real units rather than the worst case.',
  'lead-time': 'Reserve the equipment slot now or move the phase COD; confirm the quoted lead time with the OEM, and remember the order-by date is earlier still once construction, commissioning and permitting are layered on.',
};

/** Map a derived {@link PhasingRisk} to the report's {@link DetailedRisk} shape. */
export function phasingRiskToDetailedRisk(risk: PhasingRisk): DetailedRisk {
  const riskLevel: DetailedRisk['riskLevel'] =
    risk.severity === 'high' ? 'High' : risk.severity === 'medium' ? 'Medium' : 'Low';
  const impactLevel: DetailedRisk['impactLevel'] =
    risk.severity === 'high' ? 'Red' : risk.severity === 'medium' ? 'Amber' : 'Green';
  return {
    category: risk.label,
    riskLevel,
    impactLevel,
    reason: risk.description,
    action: PHASING_RISK_MITIGATION[risk.category],
  };
}

/**
 * The merged development-risk set: the report's own `developmentRisks` plus the
 * derived phasing risks. Pure on `report` — the single source the inline Risk
 * Assessment block and the lifted `RiskAssessmentSection` both consume so they
 * render byte-identical DOM. (Extracted from the SiteRagReportView render
 * closure of the same name.)
 */
export function mergeDevelopmentRisks(report: SiteRagReport): DetailedRisk[] {
  const base = report.developmentRisks ?? [];
  const intent =
    report.projectIntentAtGen
    ?? (report as { projectIntent?: SiteRagReport['projectIntentAtGen'] }).projectIntent
    ?? null;
  let phasing: DetailedRisk[] = [];
  try {
    const solvedPhaseContingency = resolvePromotedScenarioPhaseContingency({
      projectIntentAtGen: intent,
      generationImpactScenarioAtGen: report.generationImpactScenarioAtGen,
      generationImpactProjection: report.generationImpactProjection,
    });
    const blockCounts = solvedPhaseContingency
      ? blockCountsFromScenarioUnits(report.generationImpactScenarioAtGen?.input?.units ?? [])
      : {};
    phasing = derivePhasingRisks(intent, new Date(), {
      blockCounts,
      solvedPhaseContingency,
    }).map(phasingRiskToDetailedRisk);
  } catch {
    phasing = [];
  }
  return [...base, ...phasing];
}
